import React, { FC, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { ListPanelWrapper } from '@eika/link-panel';
import { IconTypes } from '@eika/icon';
import { StandardButton } from '@eika/button';
import { RouteComponentProps } from 'react-router';
import { Label } from '@eika/label';
import LabelTypes from '@eika/label/src/LabelTypes';
import { formatAmount, getPageAppending } from '../../../../../common/util/Functions';
import { useStorebandInfo } from '../../../../../common/util/Hook';
import AppMessage from '../../../../../common/AppMessage/AppMessage';
import { HeaderLink, Spinner } from '../../../../../common/share/ShareComponents';
import '../../../Arbeidsgiver.scss';
import '../../Storebrand.scss';
import '../SavingsProfiles.scss';
import { State } from '../../../../../domain/State';
import safeArray from '../../../util/SafeArray';
import '@eika/layout-style';
import Grid from '../../../../../common/Grid/Grid';
import ListPanelWithContent from '../../../../../common/ExtendedStyleguide/ListPanelWithContent';
import { StorebrandContractFund } from '../../../../../domain/Storebrand';
import FundRiskClassIndicator from '../FundRiskClassIndicator';
import LargeLinkCardWithAction from '../../../../../common/ExtendedStyleguide/LargeLinkCardWithAction';
import { RouterConfig } from '../../../../../app/Router';
import CantChangeProfileModal from '../CantChangeProfileModal';
import { DownscalingModal } from '../DownscalingModal';
import { useTranslate } from '../../../../../hooks/useTranslate';
import { PensionProduct } from "../../types";

type SavingProfilePageProps = RouteComponentProps<{ id?: string }> & RouterConfig;

const SavingProfilePage: FC<SavingProfilePageProps> = (props) => {
    const translate = useTranslate();

    const storebrandState = useSelector((state: State) => state?.Storebrand);
    const appState = useSelector((state: State) => state?.App);
    const pensionState = useSelector((state: State) => state?.Pensjon);

    const productDescription = useMemo(
        () =>
            pensionState?.arbeidsgiversPensjonsSparing?.find(
                (product: PensionProduct) => product.agreementNumber === props.match.params.id
            ).productDescription,
        [pensionState, props.match.params.id]
    );

    useStorebandInfo({ Storebrand: storebrandState }, props.action);

    const appending = useMemo(() => getPageAppending(props), []);
    const [loading] = useState(false);
    const [downscalingModalOpen, setDownscalingModalOpen] = useState(false);
    const [cantSwitchModalOpen, setCantSwitchModalOpen] = useState(false);

    const agreementInfo = useMemo(
        () => storebrandState?.info?.[props.match?.params.id || ''],
        [storebrandState, props.match?.params.id]
    );

    const profileSwitchNotAllowedReasons = useMemo(() => {
        return safeArray(agreementInfo?.profileSwitchStates)
            .filter((state) => !state.allowed)
            .map((state) => state.reason);
    }, [agreementInfo]);

    const profileSwitchIsAllowed = useMemo(
        () => !profileSwitchNotAllowedReasons.length,
        [profileSwitchNotAllowedReasons]
    );

    const changeProfile = () => {
        if (profileSwitchIsAllowed) {
            window.location.href = `${location.href}/switch`;
        } else {
            setCantSwitchModalOpen(true);
        }
    };

    const funds = useMemo(() => {
        return safeArray(
            agreementInfo?.funds.sort(
                (fundA: StorebrandContractFund, fundB: StorebrandContractFund) => fundB.marketValue - fundA.marketValue
            )
        );
    }, [agreementInfo]);

    const riskReductionCard = (
        <>
            {agreementInfo?.riskReductionInProgress && (
                <LargeLinkCardWithAction
                    background="light"
                    buttonText="Om nedtrapping"
                    title="Nedtrappingen er igang"
                    icon={IconTypes.KLOKKE_36}
                    text="Vi gjør klart for uttak av pensjon."
                    buttonType="negative"
                    action={() => setDownscalingModalOpen(true)}
                />
            )}
        </>
    );

    return (
        <div className={`saving-profile-page ${appending || 'none-appending'}`}>
            {funds.length === 1 && <Redirect to={`spareprofile/${funds[0].isin}`} />}
            <Grid
                namespace="sparing-top"
                list={[
                    {
                        layout: 'twelve',
                        element: <HeaderLink length={1} text={productDescription} />,
                    },
                ]}
            />
            <Grid
                namespace="sparing-header"
                list={[
                    {
                        layout: 'twelve',
                        element: (
                            <>
                                <div className="only-for-mobile">{riskReductionCard}</div>
                                <h1>Spareprofiler</h1>
                            </>
                        ),
                    },
                ]}
            />

            <Grid
                list={[
                    {
                        layout: 'seven',
                        element: (
                            <>
                                <AppMessage appMessage={appState?.serviceMessage} context="STOREBRAND_RISK_PROFILE" />
                                {funds && !!funds.length && (
                                    <ListPanelWrapper style={{ marginTop: 24 }}>
                                        {funds.map(
                                            (fund: StorebrandContractFund) =>
                                                fund && (
                                                    <ListPanelWithContent
                                                        key={fund.isin}
                                                        url={`${location.href}/${fund.isin}`}
                                                        title={fund.name}
                                                        textOne={formatAmount(fund.marketValue, true)}
                                                        labels={
                                                            fund.isin === agreementInfo?.riskReductionProfile?.isin
                                                                ? [
                                                                      <Label
                                                                          type={LabelTypes.INFO}
                                                                          text="Profilen det trappes ned til"
                                                                          key="label"
                                                                      />,
                                                                  ]
                                                                : undefined
                                                        }
                                                    >
                                                        <div className="savings-profile-info-box">
                                                            <div className="-item">
                                                                <div>{`${formatAmount(fund.totalReturnPct)} %`}</div>
                                                                <div className="-label">Avkastning</div>
                                                            </div>
                                                            {fund.riskClass && (
                                                                <div className="-item">
                                                                    <FundRiskClassIndicator riskClass={fund.riskClass} />
                                                                    {fund.riskLevel && fund.riskLevel !== 'UNKNOWN' && (
                                                                        <div className="-label">{`${translate(
                                                                            fund.riskLevel.toLowerCase()
                                                                        )} risiko`}</div>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </ListPanelWithContent>
                                                )
                                        )}
                                    </ListPanelWrapper>
                                )}

                                {agreementInfo && (
                                    <div className="change-profile-button-wrapper">
                                        <StandardButton
                                            icon={IconTypes.PENN_24}
                                            size="large"
                                            buttonType="negative"
                                            onClick={changeProfile}
                                        >
                                            {props.appTexts.get('changeProfile')}
                                        </StandardButton>
                                    </div>
                                )}

                                {loading && <Spinner type="linkPanel" />}
                            </>
                        ),
                    },
                    {
                        layout: 'four',
                        element: <div className="only-for-desktop">{riskReductionCard}</div>,
                    },
                ]}
            />
            <DownscalingModal onClose={() => setDownscalingModalOpen(false)} open={downscalingModalOpen} />
            <CantChangeProfileModal
                onClose={() => setCantSwitchModalOpen(false)}
                open={cantSwitchModalOpen}
                reasons={profileSwitchNotAllowedReasons}
            />
        </div>
    );
};

export default SavingProfilePage;
